export type TutorialBounds = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export type TutorialCardSide = 'top' | 'bottom' | 'left' | 'right';

type Viewport = { width: number; height: number };
type CardSize = { width: number; height: number };
type CardPosition = {
  left: number;
  top: number;
  side: TutorialCardSide;
};

export function positionCardNearTarget(
  bounds: TutorialBounds | null,
  viewport: Viewport,
  card: CardSize,
): CardPosition {
  const margin = 16;
  const gap = 12;
  const clampLeft = (left: number) =>
    Math.max(
      margin,
      Math.min(Math.max(margin, viewport.width - card.width - margin), left),
    );
  const clampTop = (top: number) =>
    Math.max(
      margin,
      Math.min(Math.max(margin, viewport.height - card.height - margin), top),
    );
  const centered = {
    left: clampLeft((viewport.width - card.width) / 2),
    top: clampTop((viewport.height - card.height) / 2),
  };
  if (!bounds) return { ...centered, side: 'bottom' };

  const centeredLeft = (bounds.left + bounds.right - card.width) / 2;
  const centeredTop = (bounds.top + bounds.bottom - card.height) / 2;
  const candidates = [
    ...[bounds.left, centeredLeft, bounds.right - card.width].map((left) => ({
      side: 'top' as const,
      left,
      top: bounds.top - card.height - gap,
      room: bounds.top - margin - card.height,
    })),
    ...[bounds.left, centeredLeft, bounds.right - card.width].map((left) => ({
      side: 'bottom' as const,
      left,
      top: bounds.bottom + gap,
      room: viewport.height - margin - bounds.bottom - card.height,
    })),
    ...[bounds.top, centeredTop, bounds.bottom - card.height].map((top) => ({
      side: 'left' as const,
      left: bounds.left - card.width - gap,
      top,
      room: bounds.left - margin - card.width,
    })),
    ...[bounds.top, centeredTop, bounds.bottom - card.height].map((top) => ({
      side: 'right' as const,
      left: bounds.right + gap,
      top,
      room: viewport.width - margin - bounds.right - card.width,
    })),
  ].map((candidate, index) => {
    const left = clampLeft(candidate.left);
    const top = clampTop(candidate.top);
    const overlapWidth = Math.max(
      0,
      Math.min(left + card.width, bounds.right) - Math.max(left, bounds.left),
    );
    const overlapHeight = Math.max(
      0,
      Math.min(top + card.height, bounds.bottom) - Math.max(top, bounds.top),
    );
    const gapX = Math.max(
      bounds.left - (left + card.width),
      left - bounds.right,
      0,
    );
    const gapY = Math.max(
      bounds.top - (top + card.height),
      top - bounds.bottom,
      0,
    );
    const displacement =
      Math.abs(left - candidate.left) + Math.abs(top - candidate.top);
    return {
      side: candidate.side,
      left,
      top,
      score:
        overlapWidth * overlapHeight * 1_000_000 +
        displacement * 10_000 +
        Math.hypot(gapX, gapY) * 100 -
        Math.max(0, candidate.room) * 0.01 +
        index * 0.001,
    };
  });
  const best = candidates.reduce((current, candidate) =>
    candidate.score < current.score ? candidate : current,
  );
  return { left: best.left, top: best.top, side: best.side };
}

export function cardAnchor(
  side: TutorialCardSide,
  marker: { x: number; y: number },
  card: { left: number; top: number; width: number; height: number },
) {
  if (side === 'top' || side === 'bottom')
    return {
      x: Math.max(
        card.left + 24,
        Math.min(card.left + card.width - 24, marker.x),
      ),
      y: side === 'top' ? card.top + card.height : card.top,
    };
  return {
    x: side === 'left' ? card.left + card.width : card.left,
    y: Math.max(card.top + 24, Math.min(card.top + card.height - 24, marker.y)),
  };
}
